import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import {format} from 'date-fns';
import Joi from 'joi';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import CustomButton from '../../components/CustomButton/CustomButton';
import CustomSnackbar from '../../components/CustomSnackBar/CustomSnackBar';
import CustomToolTip from '../../components/CustomToolTip/CustomToolTip';
import FileUpload from '../../components/FileUpload/FileUpload';
import Legend from '../../components/Legend/Legend';
import {ConfigConstant} from '../../constants/config';
import {DownloadIcon} from '../../constants/icons';
import {pxToRem} from '../../utils';
import {
  getVirtualStoreDetails,
  getChannelDetails,
  createOutlets,
  getFailureReason,
  createAncillary,
} from '../../utils/outlet';

const StyledTableHead = styled('thead')(({theme}) => ({
  display: 'table-header-group',
  background: theme.palette.primary.light,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const StyledTableHeadCell = styled('th')(({theme, filterable}) => ({
  color: theme.palette.primary.dark2,
  padding: `${pxToRem(12)} ${pxToRem(8)}`,
  cursor: filterable ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: filterable ? theme.palette.primary.medium2 : 'initial',
  },
  maxWidth: pxToRem(250),
  whiteSpace: 'nowrap',
  textAlign: 'left',
}));

const StyledTable = styled('table')(({theme}) => ({
  width: '100%',
  display: 'table',
  borderSpacing: 0,
  borderCollapse: 'collapse',
}));

const StyledTableContainerDiv = styled('div')(({theme}) => ({
  fontSize: '12px',
  boxShadow: theme.palette.boxShadow.type4,
  tableLayout: 'fixed',
  backgroundColor: theme.palette.white,
  color: theme.palette.black,
  width: '100%',
  height: '72vh',
  borderRadius: pxToRem(4),
  overflowX: 'auto',
  marginTop: pxToRem(20),
}));

const StyledDialog = styled(Dialog)(({theme}) => ({
  '& .MuiDialogTitle-root': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark2,
  },
  '& .MuiDialogContent-root': {
    padding: '16px',
  },
  '& .MuiButton-root': {
    textTransform: 'none',
    margin: '8px 8px',
  },
}));

const StyledRightDiv = styled('div')(({theme}) => ({
  display: 'inline-flex',
  float: 'right',
}));

const StyledDownloadIcon = styled(DownloadIcon)(({theme, color}) => ({
  height: 40,
  paddingTop: 10,
  marginLeft: theme.spacing(0.15),
  '& path': {
    fill: theme.palette.primary.main,
  },
  '&:hover': {
    cursor: 'pointer',
  },
}));

const BulkInsert = ({open, onClose, uploadType}) => {
  const commonFilters = useSelector((store) => store?.commonFilters);
  const {email} = useSelector((state) => state.auth);
  const [fileData, setFileData] = useState(null);
  const [isFileUploaded, setFileUpload] = useState(false);
  const [isValidating, setValidating] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [fileSaved, setFileSaved] = useState(false);
  const [legends, setLegends] = useState([]);
  const [downloadInfo, setDownloadInfo] = useState(false);

  const theme = useTheme();
  const fileHeaders =
    uploadType === 'bulk'
      ? ['partnerStoreCode', 'channel', 'channelStoreCode', 'channelBrandAuthKey']
      : ['partnerStoreCode', 'channel', 'stationCode', 'cutOffTime', 'allowedPaymentMode', 'latitude', 'longitude'];
  const authKeyRequiredForChannels = ConfigConstant.MONGO_CONFIG?.BRAND_AUTH_KEY_REQUIRED_FOR_CHANNELS;
  const schema =
    uploadType === 'bulk'
      ? Joi.object({
          partnerStoreCode: Joi.string().length(12).pattern(/^VST/).required(),
          channelStoreCode: Joi.string().required(),
          channel: Joi.string().required(),
          channelBrandAuthKey: Joi.string().when('channel', {
            is: Joi.string()
              .insensitive()
              .valid(...authKeyRequiredForChannels),
            then: Joi.required(),
            otherwise: Joi.optional().allow(''),
          }),
        })
      : Joi.object({
          partnerStoreCode: Joi.string().length(12).pattern(/^VST/).required(),
          channel: Joi.string().required(),
          stationCode: Joi.string().required(),
          allowedPaymentMode: Joi.string().required(),
          cutOffTime: Joi.number().required(),
          latitude: Joi.number().required(),
          longitude: Joi.number().required(),
        });

  const getOutletDetails = async (fileData) => {
    setValidating(true);
    const outlets = fileData.map((row) => {
      const channelDetails = getChannelDetails(row.channel, commonFilters);
      return {
        ...row,
        ...channelDetails,
        partnerCode: 'ODPT00002',
        mappingStatus: 'active',
        createdBy: email,
        updatedBy: email,
      };
    });
    const data = outlets.filter((outlet) => outlet.error);
    const validOutlets = outlets.filter((outlet) => !outlet.error);
    const batchSize = ConfigConstant.MONGO_CONFIG?.GET_OUTLET_DETAILS_PARALLEL_LIMIT;
    const batches = Math.ceil(validOutlets.length / batchSize);
    for (let batch = 0; batch < batches; batch++) {
      const outletData = validOutlets.slice(batch * batchSize, (batch + 1) * batchSize);
      const promises = outletData.map(async (outlet) => {
        const response = await getVirtualStoreDetails(
          outlet.partnerStoreCode,
          outlet.channelName,
          commonFilters.countryCode
        );
        return {...outlet, ...response};
      });
      const results = await Promise.allSettled(promises);
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          data.push(result.value);
        }
      });
    }
    setValidating(false);
    return data;
  };

  const getAncillaryData = (sheetData, partnerStoreCodes) => {
    const virtualStores = partnerStoreCodes.map((store) => store.label);
    const ancillaryDetails = sheetData.map((data) => {
      const channelData = getChannelDetails(data.channel, commonFilters);
      if (!virtualStores.includes(data.partnerStoreCode)) {
        return {
          ...data,
          ...channelData,
          error: 1,
          message: 'Store Mapping Not Present.',
        };
      }
      return {
        ...data,
        ...channelData,
        createdBy: email,
        updatedBy: email,
      };
    });
    return ancillaryDetails;
  };
  const downloadResult = () => {
    if (!fileData || fileData.length === 0) {
      return;
    }
    const headers = Object.keys(
      fileData.reduce((prev, curr) => (Object.keys(curr).length > Object.keys(prev).length ? curr : prev), {})
    );
    const visibleHeaders = [
      'status',
      'message',
      'insertId',
      ...headers.filter((header) => !['error', 'channel', 'message', 'status', 'insertId'].includes(header)),
    ];
    const csvContent = [
      visibleHeaders.join(','),
      ...fileData.map((obj) => visibleHeaders.map((key) => obj[key]).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `queryResult-${format(new Date(), 'dd/MM/yyyy')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const displayLegends = (successCount, errorCount) => {
    setLegends([
      {
        label: 'Success:',
        data: `${successCount}`,
        textColor: theme.palette.success.light,
      },
      {
        label: 'Failed:',
        data: `${errorCount}`,
        textColor: theme.palette.error.light,
      },
    ]);
  };

  useEffect(() => {
    if (fileSaved && fileData) {
      downloadResult();
      setDownloadInfo(true);
    }
  }, [fileSaved]);

  useEffect(() => {
    if (!open) {
      setFileData(null);
      setFileSaved(false);
      setLegends([]);
      setSaving(false);
      setFileUpload(false);
      setValidating(false);
      setDownloadInfo(false);
    }
  }, [open, onClose]);

  useEffect(async () => {
    if (isFileUploaded && fileData) {
      let result;
      let outlets;
      if (uploadType === 'bulk') {
        outlets = await getOutletDetails(fileData);
        setSaving(true);
        result = await createOutlets(outlets);
        setSaving(false);
      } else {
        setValidating(true);
        outlets = getAncillaryData(fileData, commonFilters.partnerStoreCode);
        setValidating(false);
        setSaving(true);
        result = await createAncillary(outlets);
        setSaving(false);
      }
      if (result.success === false) {
        const data = outlets.map((row) => {
          if (row.error) {
            return {
              ...row,
              status: 'failed',
            };
          } else {
            return {
              ...row,
              status: 'failed',
              error: 1,
              message: `${result.message?.replace(/,/g, ' ') || 'Something Went Wrong'}`,
            };
          }
        });
        displayLegends(0, outlets.length);
        setFileData(data);
        setFileSaved(true);
        // onClose();
        return;
      }
      let errorCount = 0;
      let successCount = 0;
      const updatedFileData = outlets.map((data) => {
        if (data.error) {
          errorCount += 1;
          return {
            ...data,
            status: 'failed',
          };
        }
        const resultStatus =
          uploadType === 'bulk'
            ? result.data[`${data.partnerStoreCode}-${data.channelGroupCode}-${data.channelCode}`]
            : result.data[`${data.partnerStoreCode}-${data.channelCode}-${data.stationCode}`];
        if (resultStatus) {
          errorCount = resultStatus.status === 'rejected' ? errorCount + 1 : errorCount;
          successCount = resultStatus.status === 'fulfilled' ? successCount + 1 : successCount;
          return {
            ...data,
            error: resultStatus.status === 'fulfilled' ? 0 : 1,
            status: resultStatus.status === 'fulfilled' ? 'success' : 'failed',
            insertId: resultStatus.status === 'fulfilled' ? resultStatus.insertId : null,
            message:
              resultStatus.status === 'fulfilled'
                ? uploadType === 'bulk'
                  ? 'Store Created Successfully'
                  : 'Ancillary Mapping Created Successfully'
                : getFailureReason(resultStatus.message),
          };
        }
        return data;
      });
      const sortedFileData = updatedFileData.sort((a, b) => b.error - a.error);
      setFileData(sortedFileData);
      displayLegends(successCount, errorCount);
      setFileSaved(true);
    }
  }, [isFileUploaded]);

  const renderTable = () => {
    const headers = fileData.reduce(
      (prev, curr) => (Object.keys(curr).length > Object.keys(prev).length ? curr : prev),
      {}
    );
    const visibleHeaders = Object.keys(headers).filter(
      (header) =>
        ![
          'error',
          'channel',
          'message',
          'status',
          'insertId',
          'createdBy',
          'updatedBy',
          'partnerCode',
          'mappingStatus',
        ].includes(header)
    );

    return (
      <Box>
        <StyledTableContainerDiv>
          <StyledTable>
            <StyledTableHead>
              <TableRow>
                <StyledTableHeadCell>Status</StyledTableHeadCell>
                <StyledTableHeadCell>Message</StyledTableHeadCell>
                <StyledTableHeadCell>InsertId</StyledTableHeadCell>
                {visibleHeaders.map((header) => (
                  <StyledTableHeadCell key={header}>
                    {header
                      .split('_')
                      .map((ele) => ele[0].toUpperCase() + ele.slice(1))
                      .join(' ')}
                  </StyledTableHeadCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {fileData.map((row, rowIndex) => {
                const rowKey = `${row.channelCode}-${row.channelGroupCode}-${row.partnerStoreCode}`;
                return (
                  <TableRow
                    key={rowKey}
                    style={{
                      backgroundColor: row.error ? theme.palette.error.light3 : '',
                    }}
                  >
                    <TableCell
                      key={'status'}
                      sx={{
                        fontSize: pxToRem(12),
                      }}
                    >
                      {row['status']}
                    </TableCell>
                    <TableCell
                      key={'message'}
                      sx={{
                        fontSize: pxToRem(12),
                      }}
                    >
                      {row['message']}
                    </TableCell>
                    <TableCell
                      key={'insertId'}
                      sx={{
                        fontSize: pxToRem(12),
                      }}
                    >
                      {row['insertId']}
                    </TableCell>
                    {visibleHeaders.map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: pxToRem(12),
                        }}
                      >
                        {row[header]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </StyledTableContainerDiv>
      </Box>
    );
  };

  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth={fileData ? 'bg' : 'sm'} fullWidth>
        <DialogTitle>
          <Box display="inline" flexDirection="column" alignItems="left">
            {!isFileUploaded ? (
              <Typography display="inline" variant="h6">
                Upload File
              </Typography>
            ) : (
              <Typography display="inline" variant="h5">
                Report
              </Typography>
            )}
            <StyledRightDiv>
              {fileSaved && legends.length
                ? legends.map((legend, index) => {
                    return (
                      <Legend key={index} label={legend?.label} data={legend?.data} textColor={legend?.textColor} />
                    );
                  })
                : null}
              {fileSaved && (
                <CustomToolTip title={'Dowload Result'} placement={'top-end'} arrow={true}>
                  <StyledDownloadIcon onClick={downloadResult} />
                </CustomToolTip>
              )}
            </StyledRightDiv>
          </Box>
        </DialogTitle>
        {isValidating && <LinearProgress severity="info">Validating data, please wait...</LinearProgress>}
        {isSaving && <LinearProgress severity="info">Saving data, please wait...</LinearProgress>}
        <DialogContent>
          {!fileSaved ? (
            <FileUpload
              isFileUpload={isFileUploaded}
              setFileUpload={setFileUpload}
              setFileData={setFileData}
              setValidating={setValidating}
              expectedHeaders={fileHeaders}
              validationSchema={schema}
            />
          ) : (
            renderTable()
          )}
        </DialogContent>
        {isValidating && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="body1" sx={{marginTop: 1, transition: 'margin-top 0.5s'}}>
              Validating data, please wait...
            </Typography>
          </Box>
        )}
        {isSaving && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="body1" sx={{marginTop: 1, transition: 'margin-top 0.5s'}}>
              Saving data, please wait..
            </Typography>
          </Box>
        )}
        {fileData && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CustomButton
              onClick={() => {
                onClose();
                setFileData(null);
                setFileSaved(false);
                setFileUpload(false);
              }}
              text="Close"
              color="primary"
              variant="contained"
            />
          </Box>
        )}
        {downloadInfo && (
          <CustomSnackbar
            isOpen={downloadInfo}
            autoHideDuration={4000}
            isAlert={true}
            severity={'success'}
            handleClose={() => setDownloadInfo(false)}
            message={'Report Downloaded Successfully'}
            anchorOriginVertical={'top'}
          />
        )}
      </StyledDialog>
    </>
  );
};

export default BulkInsert;
